import { GraduationCap, LogOut, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAdminTenants, useDeleteTenant } from '@/hooks/use-admin'
import { useAuth } from '@/lib/auth'

export default function AdminTenantsPage() {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const { data, isLoading } = useAdminTenants()
  const deleteTenant = useDeleteTenant()

  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [confirmInput, setConfirmInput] = useState('')

  const tenants = data?.data ?? []

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const handleDelete = () => {
    if (!deleteTarget || confirmInput !== deleteTarget.name) return
    deleteTenant.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null)
        setConfirmInput('')
      },
    })
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <GraduationCap className="h-8 w-8 text-emerald-600" />
            <span className="text-xl font-bold">LMS 管理</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            ログアウト
          </Button>
        </div>
      </header>
      <main className="container mx-auto px-4 py-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">テナント管理</h1>
            <Link to="/admin/tenants/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                新規作成
              </Button>
            </Link>
          </div>

          <Card>
            <CardContent className="pt-6">
              {isLoading ? (
                <Skeleton className="h-48" />
              ) : tenants.length === 0 ? (
                <p className="text-muted-foreground">テナントがありません。</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>名前</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>作成日</TableHead>
                      <TableHead>操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tenants.map((tenant: any) => (
                      <TableRow key={tenant.id}>
                        <TableCell className="font-medium">{tenant.name}</TableCell>
                        <TableCell>{tenant.slug}</TableCell>
                        <TableCell>
                          {new Date(tenant.createdAt).toLocaleDateString('ja-JP')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Link to={`/admin/tenants/${tenant.id}/edit`}>
                              <Button variant="outline" size="sm">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setDeleteTarget({ id: tenant.id, name: tenant.name })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Delete confirmation dialog */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <Card className="w-full max-w-md">
              <CardContent className="pt-6 space-y-4">
                <h2 className="text-lg font-bold text-destructive">テナント削除</h2>
                <p className="text-sm text-muted-foreground">
                  テナント「<strong>{deleteTarget.name}</strong>」を削除します。
                  この操作は取り消せません。所属するすべてのユーザー・コース・データが削除されます。
                </p>
                <p className="text-sm">
                  確認のため、テナント名「<strong>{deleteTarget.name}</strong>」を入力してください。
                </p>
                <Input
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  placeholder={deleteTarget.name}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDeleteTarget(null)
                      setConfirmInput('')
                    }}
                  >
                    キャンセル
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={confirmInput !== deleteTarget.name || deleteTenant.isPending}
                    onClick={handleDelete}
                  >
                    {deleteTenant.isPending ? '削除中...' : '削除する'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  )
}
